import { cx } from '@emotion/css';
import {
  faEllipsis,
  faPenToSquare,
  faTrashCan,
} from '@fortawesome/free-solid-svg-icons';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Button } from 'apps/shared/scripts/components';
import { useDeletePost } from 'apps/shared/scripts/graphql';
import { useFormCallback } from 'apps/shared/scripts/hooks';
import { ModalTypes, openModal } from 'apps/shared/scripts/redux';

import {
  buttonClassName,
  dangerClassName,
  iconClassName,
  infoClassName,
  postActionsClassName,
  rotatedIconClassName,
  rowClassName,
} from './styles';

interface Props {
  id: string;
  isCardDelivered?: boolean;
  isCardOwner?: boolean;
  isPostOwner?: boolean;
}

const CardPostActions = ({
  id: postId,
  isCardDelivered,
  isCardOwner,
  isPostOwner,
}: Props) => {
  const [isVisible, setVisibility] = useState<boolean>(false);
  const dispatch = useDispatch();
  const { mutate: deletePost, isMutating: isDeleting } = useDeletePost({
    refetchQueries: ['PostsByCardId'],
    awaitRefetchQueries: true,
  });
  const { isSuccess, onSuccess, onError } = useFormCallback();

  const handleToggle = useCallback(() => {
    setVisibility(!isVisible);
  }, [isVisible]);

  const onEdit = useCallback(() => {
    dispatch(openModal({ type: ModalTypes.EditCardPost, id: postId }));
    setTimeout(() => setVisibility(false), 100);
  }, [dispatch, postId]);

  const onDelete = useCallback(async () => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        const { result, errors } = await deletePost({ id: postId });
        if (!result || errors.length > 0) {
          throw new Error(errors[0].message);
        }
        onSuccess({ message: 'Success! Post deleted' });
      } catch (e) {
        onError(e);
        throw e;
      }
    }
  }, [deletePost, onError, onSuccess, postId]);

  // hide if not card owner or post owner
  if (!isPostOwner && !isCardOwner) {
    return null;
  }

  // hide if card delivered already
  if (isCardDelivered) {
    return null;
  }

  return (
    <div className={postActionsClassName}>
      <div className={rowClassName}>
        <Button
          className={buttonClassName}
          iconClassName={cx(iconClassName, {
            [rotatedIconClassName]: isVisible,
          })}
          faIcon={faEllipsis}
          onClick={handleToggle}
        />
        {isVisible && (
          <div className={rowClassName}>
            {isPostOwner && (
              <Button
                className={cx(buttonClassName, infoClassName)}
                faIcon={faPenToSquare}
                onClick={onEdit}
              />
            )}
            <Button
              className={cx(buttonClassName, dangerClassName)}
              disabled={isDeleting}
              faIcon={faTrashCan}
              loading={isDeleting}
              onClick={onDelete}
              success={isSuccess}
            />
          </div>
        )}
      </div>
    </div>
  );
};

CardPostActions.displayName = 'CardPostActions';

export default CardPostActions;
